import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth.store';
import { useUIStore } from '../../stores/ui.store';
import { Icon, type IconName } from '../ui/Icon';
import { useQuery } from '@tanstack/react-query';
import { useActionStream } from '../../features/oggi/useActionStream';
import { tasksSummaryQuery } from '../../features/tasks/tasksApi';
import { ModuleKey, hasAtLeast, PermissionLevel } from '@masamune/shared';
import { cn } from '../../lib/utils';
import { isFrozen } from '../../lib/frozen';

/**
 * Magic-move rail: una singola pill bianca assoluta che SCIVOLA tra le voci
 * attive invece di saltare. Misura l'item [aria-current] più profondo e
 * posiziona la pill via transform/height con transition. Stile identico al
 * pixel, si aggiunge solo il movimento (firma percettiva nav 2026).
 */
function useActivePill(navRef: React.RefObject<HTMLElement>, deps: unknown[]) {
  const [pill, setPill] = useState<{ top: number; height: number; visible: boolean }>({ top: 0, height: 0, visible: false });
  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const measure = () => {
      const actives = nav.querySelectorAll<HTMLElement>('[aria-current="true"]');
      const target = actives[actives.length - 1]; // il più profondo (sub-item se presente)
      if (!target) { setPill((p) => ({ ...p, visible: false })); return; }
      const navRect = nav.getBoundingClientRect();
      const r = target.getBoundingClientRect();
      setPill({ top: r.top - navRect.top + nav.scrollTop, height: r.height, visible: true });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(nav);
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return pill;
}

/**
 * "Gelatina" al cambio voce (liquid, come la tab bar iOS): quando la pill si
 * sposta, il vetro si allunga nella direzione del viaggio e si assesta con un
 * piccolo rimbalzo, in proporzione alla DISTANZA percorsa (voce adiacente:
 * appena percettibile; salto lungo: visibile ma composto). Lo scale vive sul
 * figlio-vetro via Web Animations API, così non litiga col translateY della
 * pill; la durata dello slide segue anch'essa la distanza. Niente in
 * prefers-reduced-motion.
 */
function usePillSpring(glassRef: React.RefObject<HTMLElement>, top: number, visible: boolean) {
  const prevTop = useRef<number | null>(null);
  useEffect(() => {
    if (!visible) { prevTop.current = null; return; }
    const from = prevTop.current;
    prevTop.current = top;
    const el = glassRef.current;
    if (from == null || from === top || !el || typeof el.animate !== 'function') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    const dist = Math.abs(top - from);
    const t = Math.min(1, dist / 480);              // 0 = adiacente, 1 = salto lungo
    const stretch = 0.05 + 0.13 * t;                // allungamento lungo il viaggio
    const squeeze = 0.012 + 0.03 * t;               // strizzata laterale
    const duration = Math.round(320 + 260 * t);     // slide più lunga per salti più lunghi
    const down = top > from;

    // Lo slide della pill segue la stessa durata, con easing morbido "a molla"
    const pill = el.parentElement;
    if (pill) {
      pill.style.transitionDuration = `${duration}ms`;
      pill.style.transitionTimingFunction = 'cubic-bezier(0.22, 1, 0.36, 1)';
    }
    // Il vetro si stira dal lato da cui parte (origine opposta alla direzione)
    el.style.transformOrigin = down ? '50% 0%' : '50% 100%';
    const anim = el.animate(
      [
        { transform: 'scale(1, 1)' },
        { transform: `scale(${1 - squeeze}, ${1 + stretch})`, offset: 0.32 },
        { transform: `scale(${1 + squeeze * 0.5}, ${1 - stretch * 0.28})`, offset: 0.68 },
        { transform: 'scale(1, 1)' },
      ],
      { duration, easing: 'cubic-bezier(0.2, 0.9, 0.3, 1)' },
    );
    return () => anim.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [top, visible]);
}

interface NavItem {
  key: string;
  to: string;
  label: string;
  icon: IconName;
  module: ModuleKey;
  count?: number;
  /** Etichetta divisione (neutra): data hook, NON un colore — l'accento resta il viola Masamune */
  div?: 'mkt' | 'prod' | 'design' | 'talent';
  /** Sotto-voce: `module` serve solo al badge "In pausa" (Fase 0); senza, eredita quello del padre */
  sub?: { key: string; to: string; label: string; icon: IconName; module?: ModuleKey; div?: 'mkt' | 'prod' | 'design' | 'talent' }[];
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

const NAV: NavGroup[] = [
  {
    title: 'Operativo',
    items: [
      { key: 'oggi', to: '/', label: 'Oggi', icon: 'dashboard', module: ModuleKey.DASHBOARD },
      { key: 'tasks', to: '/tasks', label: 'Task', icon: 'tasks', module: ModuleKey.TASKS },
      { key: 'deals', to: '/trattative', label: 'Trattative', icon: 'handshake', module: ModuleKey.TRATTATIVE },
      // "Contatti" e non piu' "CRM": dentro ci sono clienti, potenziali e
      // professionisti insieme, e la voce deve dire quello che c'e'.
      { key: 'crm',       to: '/crm', label: 'Contatti', icon: 'users',    module: ModuleKey.CRM },
      { key: 'suppliers', to: '/suppliers', label: 'Professionisti', icon: 'brief', module: ModuleKey.SUPPLIERS },
    ],
  },
  {
    // Le tre divisioni erano tre voci per la stessa identica pagina: adesso
    // sono un filtro dentro Progetti. Talent Sourcing resta a sé perché è
    // un'altra cosa (persone e corsi, non commesse): deciso da Lorenzo il 19/9.
    // Il permesso è CRM perché è quello che già governa la creazione di un
    // progetto lato API: gatarla su MARKETING avrebbe nascosto la sezione
    // proprio a chi l'ha chiesta.
    title: 'Lavoro',
    items: [
      { key: 'progetti', to: '/progetti', label: 'Progetti', icon: 'cube', module: ModuleKey.CRM },
      { key: 'talent',   to: '/talent',   label: 'Talent Sourcing', icon: 'talent', module: ModuleKey.TALENT, div: 'talent' },
    ],
  },
  {
    title: 'Back-office',
    items: [
      // Una sola voce per i soldi: la Contabilità è confluita in Amministrazione
      // (tab "Documenti"). Le route /invoices e /quotes restano vive per i
      // vecchi link e per le pagine di dettaglio, ma fuori dal menu.
      { key: 'admin',    to: '/admin',    label: 'Amministrazione',  icon: 'landmark', module: ModuleKey.AMMINISTRAZIONE },
      // "Abbonamenti" e non "Servizi gestiti" (Lorenzo, 19/9): dentro ci sono
      // gli abbonamenti Stripe piu' i canoni fatturati a mano, e il nome deve
      // dire quello che si trova.
      { key: 'services', to: '/services', label: 'Abbonamenti',      icon: 'repeat',   module: ModuleKey.SERVICES },
      { key: 'vault',    to: '/vault',    label: 'Password Vault',   icon: 'lock',     module: ModuleKey.VAULT },
      { key: 'report',   to: '/report',   label: 'Report',           icon: 'chart',    module: ModuleKey.REPORT },
      { key: 'settings', to: '/settings', label: 'Impostazioni',     icon: 'settings', module: ModuleKey.SETTINGS },
    ],
  },
];

export function Sidebar() {
  const user = useAuthStore((s) => s.user);
  const collapsed = useUIStore((s) => s.sidebarCollapsed);
  const canvas = useUIStore((s) => s.canvas);
  const location = useLocation();
  const navRef = useRef<HTMLDivElement>(null);
  const glassRef = useRef<HTMLDivElement>(null);
  const pill = useActivePill(navRef, [location.pathname, collapsed]);
  usePillSpring(glassRef, pill.top, pill.visible);
  // Conteggio vivo per "Oggi" (critico + oggi) — stessa cache dello stream, zero fetch extra
  const { counts } = useActionStream();
  const oggiCount = counts.critical + counts.today;

  const canSee = (module: ModuleKey) => {
    if (!user) return false;
    const lvl = user.permissions.find((p) => p.module === module)?.level ?? PermissionLevel.NONE;
    return hasAtLeast(lvl, PermissionLevel.READ);
  };

  // Badge "Task" = scadono oggi + in ritardo dei MIEI task (stessa query dello
  // stream Oggi: cache condivisa, un fetch al minuto al massimo)
  const { data: taskSummary } = useQuery({ ...tasksSummaryQuery('mine'), enabled: canSee(ModuleKey.TASKS) });
  const tasksCount = (taskSummary?.dueToday ?? 0) + (taskSummary?.overdue ?? 0);

  return (
    <aside className="os-sidebar flex flex-col">
      <div className="sb-brand">
        {collapsed ? (
          <div className="sb-brand-mark" style={{ width: 'auto', padding: '0 10px' }}>
            <img src="/logos/m-white.svg" alt="Masamune" style={{ height: 14, width: 'auto', display: 'block' }} />
          </div>
        ) : (
          <>
            {/* Spina viola: sempre il logotipo bianco (quadratino viola + scritta bianca) */}
            <img src="/logos/masamune.svg" alt="Masamune" className="sb-brand-logo" />
            <div className="sb-brand-tag">OS v0.1</div>
          </>
        )}
      </div>

      <div ref={navRef} className="flex-1 py-1 overflow-y-auto relative">
        {/* Pill attiva che scivola tra le voci; il vetro (figlio) è quello che "gelatina" */}
        <div
          className="sb-active-pill"
          aria-hidden="true"
          style={{
            transform: `translateY(${pill.top}px)`,
            height: pill.height,
            opacity: pill.visible ? 1 : 0,
          }}
        >
          <div ref={glassRef} className="sb-active-pill-glass" />
        </div>
        {NAV.map((group) => {
          const visible = group.items.filter((it) => canSee(it.module));
          if (!visible.length) return null;
          return (
            <div className="sb-section" key={group.title}>
              {!collapsed && <div className="sb-section-label">{group.title}</div>}
              {visible.map((item) => {
                const isActive = location.pathname === item.to || (item.to !== '/' && location.pathname.startsWith(item.to + '/'));
                // Fase 0: la voce resta visibile e cliccabile (porta alla pagina
                // di pausa), ma smorzata e con badge "In pausa" al posto del conteggio
                const paused = isFrozen(item.module);
                return (
                  <div key={item.key}>
                    <NavLink
                      to={item.to}
                      className="sb-item"
                      data-div={item.div}
                      aria-current={isActive ? 'true' : 'false'}
                      aria-disabled={paused || undefined}
                      title={collapsed ? (paused ? `${item.label} (in pausa)` : item.label) : undefined}
                    >
                      <Icon name={item.icon} size={16} />
                      {!collapsed && (
                        <>
                          <span>{item.label}</span>
                          {paused
                            ? <span className="sb-count sb-count-paused">In pausa</span>
                            : item.key === 'oggi' && oggiCount > 0
                              ? <span className="sb-count">{oggiCount}</span>
                              : item.key === 'tasks' && tasksCount > 0
                                ? <span className="sb-count">{tasksCount}</span>
                                : item.count != null && <span className="sb-count">{item.count}</span>}
                        </>
                      )}
                    </NavLink>
                    {item.sub && !collapsed && (
                      <div className="ml-6 pl-2 border-l border-line">
                        {item.sub.map((s) => {
                          const subPaused = isFrozen(s.module ?? item.module);
                          return (
                            <NavLink key={s.key} to={s.to} className="sb-item" data-div={s.div} aria-current={location.pathname === s.to ? 'true' : 'false'} aria-disabled={subPaused || undefined}>
                              <Icon name={s.icon} size={14} />
                              <span>{s.label}</span>
                              {subPaused && <span className="sb-count sb-count-paused">In pausa</span>}
                            </NavLink>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {user && (
        <div className="sb-user">
          <div className="sb-avatar">{user.initials}</div>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-text text-[13px] truncate">{user.firstName} {user.lastName}</div>
              <span className="sb-role">{user.role.name}</span>
            </div>
          )}
        </div>
      )}
    </aside>
  );
}
